// client/src/components/event/WatchPartyConfig.jsx
/**
 * =================================================================
 * Watch Party Configuration Component
 * =================================================================
 * Follows ChallengeConfig pattern for consistency.
 * FEATURES:
 * 1. Set video URL, platform
 * 2. Schedule date/time and duration
 * 3. Display shareable hyperlink
 * 4. View registrations
 * =================================================================
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import "./AuctionConfig.css";

const WatchPartyConfig = ({ tenantId, eventId, itemId, itemName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const [config, setConfig] = useState({
    name: itemName || "Watch Party",
    description: "",
    video_url: "",
    platform: "",
    scheduled_at: "",
    duration_minutes: 120,
    status: "NEW",
  });

  const [registrations, setRegistrations] = useState([]);
  const [showRegistrations, setShowRegistrations] = useState(false);
  const [registrationCount, setRegistrationCount] = useState(0);
  const [configExists, setConfigExists] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL || "";
  const shareLink = configExists
    ? `${window.location.origin}/${tenantId}/${eventId}/${itemId}/action?type=watch_party`
    : "";

  useEffect(() => {
    loadConfig();
  }, [tenantId, eventId, itemId]);

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const formatDateTimeForBackend = (val) => {
    if (!val) return null;
    try {
      const d = new Date(val);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
      return null;
    }
  };

  const loadConfig = async () => {
    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.get(
        `${baseUrl}/api/watch-party/${tenantId}/event/${eventId}/item/${itemId}/config`
      );

      if (response.data.success && response.data.watch_party) {
        const c = response.data.watch_party;
        setConfigExists(true);
        setConfig({
          name: c.name || itemName || "Watch Party",
          description: c.description || "",
          video_url: c.video_url || "",
          platform: c.platform || "",
          scheduled_at: formatDateForInput(c.scheduled_at),
          duration_minutes: c.duration_minutes || 120,
          status: c.status || "NEW",
        });
        loadRegistrationCount();
      } else {
        setConfigExists(false);
        setMessage({
          text: "No Watch Party configured yet. Fill out the form below to create one.",
          type: "info",
        });
      }
    } catch (err) {
      console.error("Error loading Watch Party config:", err);
      if (err.response?.status === 404) {
        setMessage({
          text: "No Watch Party configured yet. Fill out the form below to create one.",
          type: "info",
        });
      } else {
        setMessage({ text: "Failed to load Watch Party configuration", type: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRegistrationCount = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/watch-party/${tenantId}/event/${eventId}/item/${itemId}/registrations`
      );
      if (response.data.success) {
        setRegistrationCount(response.data.count || response.data.registrations?.length || 0);
      }
    } catch { /* Non-critical */ }
  };

  const loadRegistrations = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/watch-party/${tenantId}/event/${eventId}/item/${itemId}/registrations`
      );
      if (response.data.success) {
        setRegistrations(response.data.registrations);
        setRegistrationCount(response.data.count || response.data.registrations.length);
        setShowRegistrations(true);
      }
    } catch (err) {
      console.error("Error loading registrations:", err);
      setMessage({ text: "Failed to load registrations", type: "error" });
    }
  };

  const handleInputChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const validateConfig = () => {
    const errors = [];
    if (!config.name || config.name.trim().length === 0) {
      errors.push("Watch Party name is required");
    }
    if (config.duration_minutes < 1) {
      errors.push("Duration must be at least 1 minute");
    }
    return errors;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = validateConfig();
    if (errors.length > 0) {
      setMessage({ text: errors.join(". "), type: "error" });
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const payload = {
        name: config.name,
        description: config.description || null,
        video_url: config.video_url || null,
        platform: config.platform || null,
        scheduled_at: formatDateTimeForBackend(config.scheduled_at),
        duration_minutes: Number(config.duration_minutes),
        status: config.status,
      };

      const response = await axios.post(
        `${baseUrl}/api/watch-party/${tenantId}/event/${eventId}/item/${itemId}/config`,
        payload
      );

      if (response.data.success) {
        setConfigExists(true);
        setMessage({ text: "Watch Party configuration saved successfully!", type: "success" });
        setTimeout(() => {
          setMessage({ text: "", type: "" });
          loadConfig();
        }, 2000);
      }
    } catch (err) {
      console.error("Error saving Watch Party config:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to save Watch Party configuration",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const copyShareLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink).then(
      () => {
        setMessage({ text: "Link copied to clipboard!", type: "success" });
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      },
      () => setMessage({ text: "Failed to copy link", type: "error" })
    );
  };

  const handleDelete = async () => {
    if (!window.confirm("Cancel this Watch Party? This cannot be undone.")) return;

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.post(
        `${baseUrl}/api/watch-party/${tenantId}/event/${eventId}/item/${itemId}/config`,
        { ...config, status: "CANCELLED" }
      );
      if (response.data.success) {
        setMessage({ text: "Watch Party cancelled successfully", type: "success" });
        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      }
    } catch (err) {
      console.error("Error cancelling Watch Party:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to cancel Watch Party",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="auction-config-container">
        <div className="loading">Loading Watch Party configuration...</div>
      </div>
    );
  }

  const isComplete = config.status === "COMPLETE" || config.status === "CANCELLED";

  return (
    <div className="auction-config-container">
      {/* Header */}
      <div className="auction-config-header">
        <h2>Watch Party Configuration</h2>
        <button className="close-button" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      {/* Share Link */}
      {shareLink && (
        <div className="share-link-section">
          <h3>Shareable Link</h3>
          <p className="share-link-description">
            Share this link for participants to register for the watch party:
          </p>
          <div className="share-link-container">
            <div className="share-link-display">
              <a href={shareLink} target="_blank" rel="noopener noreferrer">
                {shareLink}
              </a>
            </div>
            <button type="button" className="copy-link-button" onClick={copyShareLink}>
              Copy Link
            </button>
          </div>
        </div>
      )}

      {/* Statistics */}
      {configExists && (
        <div className="auction-stats-section">
          <h3>Watch Party Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-label">Registrations</div>
              <div className="stat-value">{registrationCount}</div>
            </div>
          </div>
          <div className="stats-actions">
            <button type="button" className="view-entries-button" onClick={loadRegistrations}>
              View Registrations
            </button>
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <form className="auction-config-form" onSubmit={handleSave}>
        <div className="form-group">
          <label htmlFor="name">
            Watch Party Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={config.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="Enter watch party name"
            disabled={isComplete}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={config.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            placeholder="Describe what you'll be watching together"
            disabled={isComplete}
            rows={3}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="video_url">Video / Stream URL</label>
            <input
              type="url"
              id="video_url"
              value={config.video_url}
              onChange={(e) => handleInputChange("video_url", e.target.value)}
              placeholder="https://..."
              disabled={isComplete}
            />
            <span className="form-hint">Link to the video or stream</span>
          </div>
          <div className="form-group">
            <label htmlFor="platform">Platform</label>
            <input
              type="text"
              id="platform"
              value={config.platform}
              onChange={(e) => handleInputChange("platform", e.target.value)}
              placeholder="e.g. YouTube, Netflix Party, Twitch"
              disabled={isComplete}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="scheduled_at">Scheduled Date & Time</label>
            <input
              type="datetime-local"
              id="scheduled_at"
              value={config.scheduled_at}
              onChange={(e) => handleInputChange("scheduled_at", e.target.value)}
              disabled={isComplete}
            />
          </div>
          <div className="form-group">
            <label htmlFor="duration_minutes">
              Duration (minutes) <span className="required">*</span>
            </label>
            <input
              type="number"
              id="duration_minutes"
              min="1"
              max="1440"
              value={config.duration_minutes}
              onChange={(e) => handleInputChange("duration_minutes", parseInt(e.target.value) || 120)}
              disabled={isComplete}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={config.status}
            onChange={(e) => handleInputChange("status", e.target.value)}
            disabled={isComplete}
          >
            <option value="NEW">New</option>
            <option value="ACTIVE">Active</option>
            <option value="LIVE">Live</option>
            <option value="COMPLETE">Complete</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        <div className="form-actions">
          {isComplete ? (
            <div className="completion-notice">
              This watch party is {config.status === "CANCELLED" ? "cancelled" : "complete"} - no further changes allowed
            </div>
          ) : (
            <>
              <button type="submit" className="save-button" disabled={saving}>
                {saving ? "Saving..." : "Save Configuration"}
              </button>
              {configExists && (
                <button
                  type="button"
                  className="delete-button"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  Cancel Watch Party
                </button>
              )}
            </>
          )}
        </div>
      </form>

      {/* Registrations Modal */}
      {showRegistrations && (
        <div className="modal-overlay" onClick={() => setShowRegistrations(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Registrations ({registrations.length})</h3>
              <button className="close-button" onClick={() => setShowRegistrations(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <table className="entries-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((reg, index) => (
                    <tr key={reg.id}>
                      <td>{index + 1}</td>
                      <td>{reg.name}</td>
                      <td>{reg.email}</td>
                      <td>{new Date(reg.created_on).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WatchPartyConfig;
